import React, { useState } from "react";
import { useFormState, useWatch } from "react-hook-form";

import { ToneSegments } from "../../../../components/FormPanel";
import {
  FormRow,
  SelectSheet,
  SelectSheetOption,
} from "../../../../components/MobileForm";
import {
  Card,
  CardLabel,
  RowsCard,
  HelperText,
} from "../../../../components/MobileForm/styles";
import {
  FormInput,
  FormMoneyInput,
  InputDate,
} from "../../../../components/Input";
import { localToday } from "../../../../utils";
import { validateAmount } from "../tradeValidation";
import { MobileChargeField } from "../MobileChargeField";
import { MobileNoteField } from "../MobileNoteField";
import {
  ACTION_SEGMENTS,
  FEE_HELPER,
  WITHHOLDING_TAX_HELPER,
} from "./constants";
import { HoldingOption, MobileHoldingTradeFormProps } from "./types";
import { FieldWarning } from "./styles";

export const MobileHoldingTradeForm: React.FC<MobileHoldingTradeFormProps> = ({
  control,
  register,
  setValue,
  action,
  copy,
  holdingOptions,
  minDate,
  dateRules,
  quantityRules,
  overHeldWarning,
  isSubmitting,
  onActionChange,
}) => {
  const [isHoldingSheetOpen, setIsHoldingSheetOpen] = useState(false);

  const values = useWatch({ control });
  const { errors } = useFormState({ control });

  const isSell = action === "sell";
  const isDividend = action === "dividend";

  // The position select is picked through a bottom sheet rather than a
  // Controller input, so register it here purely to attach validation;
  // handleSubmit then enforces it on submit.
  register("holding", { required: "Position is required!" });

  const handleHoldingSelect = (option: SelectSheetOption) => {
    // The sheet hands back a bare `{ label, value }`; the form field needs the
    // whole option, since the payload is built from its `holdingId` and the
    // over-held warning reads its `quantity`.
    const holding = holdingOptions.find(
      (candidate) => candidate.value === option.value,
    );
    if (holding) {
      setValue("holding", holding as HoldingOption, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    setIsHoldingSheetOpen(false);
  };

  return (
    <>
      <Card>
        <CardLabel as="span">Trade type *</CardLabel>
        <ToneSegments
          groupLabel="Trade type"
          options={ACTION_SEGMENTS}
          selectedValue={action}
          disabled={isSubmitting}
          onChange={(segment) => onActionChange(segment.value)}
        />
      </Card>

      <RowsCard>
        <FormRow
          label="Position"
          value={values.holding?.label}
          placeholder="Select position"
          error={errors.holding?.message}
          onClick={() => setIsHoldingSheetOpen(true)}
        />
      </RowsCard>
      <HelperText>{copy.positionHelper}</HelperText>

      {isSell && (
        <Card>
          <CardLabel htmlFor="mobile-holding-trade-quantity">
            Quantity *
          </CardLabel>
          <FormInput
            id="mobile-holding-trade-quantity"
            required
            name="quantity"
            control={control}
            rules={quantityRules}
            handleClear={false}
            disabled={isSubmitting}
            placeholder="e.g. 12.5"
          />
          <HelperText>Units sold, up to 6 decimal places.</HelperText>
          {overHeldWarning && (
            <FieldWarning role="status">{overHeldWarning}</FieldWarning>
          )}
        </Card>
      )}

      <Card>
        <CardLabel htmlFor="mobile-holding-trade-amount">Amount *</CardLabel>
        <FormMoneyInput
          id="mobile-holding-trade-amount"
          required
          name="amountEur"
          control={control}
          rules={{ validate: validateAmount }}
          disabled={isSubmitting}
          placeholder="0.00"
          variant="title"
        />
        <HelperText>{copy.amountHelper}</HelperText>

        {isSell && (
          <MobileChargeField
            id="mobile-holding-trade-fee"
            control={control}
            charge="fee"
            helper={FEE_HELPER}
            isSubmitting={isSubmitting}
          />
        )}
        {isDividend && (
          <MobileChargeField
            id="mobile-holding-trade-tax"
            control={control}
            charge="withholdingTax"
            helper={WITHHOLDING_TAX_HELPER}
            isSubmitting={isSubmitting}
          />
        )}
      </Card>

      <Card>
        <CardLabel htmlFor="mobile-holding-trade-date">Trade date *</CardLabel>
        <InputDate
          id="mobile-holding-trade-date"
          name="date"
          control={control}
          rules={dateRules}
          disabled={isSubmitting}
          minDate={minDate}
          maxDate={localToday()}
          placeholder="YYYY/MM/DD"
        />
        <HelperText>Must be on or after the account's start date.</HelperText>
      </Card>

      <Card>
        <MobileNoteField
          id="mobile-holding-trade-note"
          control={control}
          isSubmitting={isSubmitting}
        />
      </Card>

      <SelectSheet
        isOpen={isHoldingSheetOpen}
        title="Select position"
        options={holdingOptions}
        selectedValue={values.holding?.value}
        onSelect={handleHoldingSelect}
        onClose={() => setIsHoldingSheetOpen(false)}
      />
    </>
  );
};
